import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;
let firebaseConfig: Record<string, string> | null = null;
let initPromise: Promise<Auth> | null = null;

export async function getFirebaseAuth(): Promise<Auth> {
  if (auth) return auth;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const response = await fetch('/api/config/firebase');
      if (!response.ok) throw new Error('Failed to load Firebase config');

      const config = await response.json();
      firebaseConfig = config;

      app = initializeApp(config);
      auth = getAuth(app);

      return auth;
    } catch (error) {
      console.error('Firebase initialization error:', error);
      throw error;
    }
  })();

  return initPromise;
}

/** Returns the Firebase app instance (ensures init has run). */
export async function getFirebaseApp(): Promise<FirebaseApp> {
  await getFirebaseAuth();
  return app!;
}

/** Returns the raw config object (ensures init has run). */
export async function getFirebaseConfig(): Promise<Record<string, string>> {
  await getFirebaseAuth();
  return firebaseConfig!;
}

/** Firestore client SDK handle — lazy-initialised on first call. Used by
 *  the mentions real-time listener; comments still go through the HTTP
 *  API, so this is the only place in the app that talks to Firestore
 *  directly. If more read paths move over, security rules must be
 *  updated to cover every collection the client reads.
 *
 *  Database id: this project uses a NAMED Firestore database ("mosaics"),
 *  not the implicit "(default)" one. Omitting the id makes the SDK ask
 *  for "(default)" and fail with
 *      Firestore (…): Database '(default)' not found.
 *  Overridable via VITE_FIRESTORE_DATABASE_ID so a different environment
 *  (local emulator, staging) can swap in its own database name without a
 *  code change. */
const FIRESTORE_DATABASE_ID = import.meta.env.VITE_FIRESTORE_DATABASE_ID || 'mosaics';
export async function getFirebaseFirestore(): Promise<Firestore> {
  if (firestore) return firestore;
  await getFirebaseAuth();   // ensures `app` is populated
  firestore = getFirestore(app!, FIRESTORE_DATABASE_ID);
  return firestore;
}